import { useEffect, useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { TanTable } from '../../components/TanTable';
import { getEquipmentReport, type EquipmentReportData, type EquipmentReportRow } from '../../services/equipmentReport.service';

const visibleColumns = new Set([
  'Unit #', 'Category', 'In Service', 'Market', 'Last Moved (UTC)',
  'Last Seen (UTC)', 'Last Seen Location', 'Last Order', 'GPS Source',
  'Year', 'Make', 'Model', 'VIN', 'Last Inspection', 'Next Inspection',
]);

export default function EquipmentReport() {
  const [report, setReport] = useState<EquipmentReportData>({ rows: [], columns: [] });
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [revision, setRevision] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setReport({ rows: [], columns: [] });
    getEquipmentReport(includeDeleted, controller.signal)
      .then(data => { if (!controller.signal.aborted) setReport(data); })
      .catch(() => { if (!controller.signal.aborted) setError('Unable to load the equipment report. Please try again.'); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [includeDeleted, revision]);

  const columns = useMemo<ColumnDef<EquipmentReportRow>[]>(() => report.columns.map(key => ({
    id: key,
    accessorFn: row => row[key] ?? '',
    header: key,
    size: key === 'Last Seen Location' ? 280 : 170,
    cell: ({ getValue, row }) => {
      const value = String(getValue() ?? '');
      if (key === 'Unit #' && value) {
        return <a className="text-blue-600 dark:text-blue-400 hover:underline" href={`/data-management/equipment-list?search=${encodeURIComponent(value)}`}>{value}</a>;
      }
      if (key === 'Last Seen Location' && value && row.original.Latitude != null && row.original.Longitude != null) {
        return <a className="text-blue-600 dark:text-blue-400 hover:underline" target="_blank" rel="noopener noreferrer" href={`https://www.google.com/maps/search/?api=1&query=${row.original.Latitude},${row.original.Longitude}`}>{value}</a>;
      }
      return value;
    },
    // Export the underlying scalar, including identifiers with leading zeroes.
    meta: { exportValue: (row: EquipmentReportRow) => row[key] ?? '' },
  })), [report.columns]);
  const visibility = useMemo(() => Object.fromEntries(report.columns.map(key => [key, visibleColumns.has(key)])), [report.columns]);
  const exportColumns = useMemo(() => report.columns.map(key => ({ key, header: key })), [report.columns]);

  return <div className="p-4 space-y-4">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <h1 className="text-xl font-semibold text-label">Equipment Report</h1>
      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2 text-sm text-label">
          <input type="checkbox" checked={includeDeleted} onChange={event => setIncludeDeleted(event.target.checked)} />
          Include deleted equipment
        </label>
        <button className="rounded border px-3 py-1.5 text-sm text-label disabled:opacity-50" disabled={loading} onClick={() => setRevision(value => value + 1)}>Refresh</button>
      </div>
    </div>
    <p className="text-sm text-muted">
      All GWI equipment, including out-of-service units. Filter any column and use Excel export to download all fields for the matching rows.
      {' '}GPS times are UTC. Last moved is the latest movement observed in retained GPS data; blank means unknown.
      {' '}BlackBerry and Samsara equipment feeds retain their latest reading; Samsara vehicle history is a rolling cache.
      {' '}Last order is the newest order by creation date with this unit recorded as chassis or container; order dates use the CMS time zone.
    </p>
    {error && <div role="alert" className="rounded border border-red-300 bg-red-50 p-3 text-red-700 dark:bg-red-950 dark:text-red-200">{error}</div>}
    <TanTable<EquipmentReportRow>
      data={report.rows} columns={columns} loading={loading}
      settingsKey="equipment-report" cardTitle="GWI Equipment" exportName="GWI Equipment Report"
      enableColumnFilters enableExcelExport enableZebraStriping
      searchPlaceholder="Search equipment, location, VIN or order..."
      defaultColumnVisibility={visibility} exportColumnOrder={exportColumns}
      lockedColumns={['Unit #']} getRowId={row => String(row.recno)}
    />
  </div>;
}
